import {
  ExtendedWarrantyPremiumAutoCanadaDigital,
  IAExtendedWarrantyPrograms,
} from "@modules/purchase/warranty/types/IAProgram";
import { useTranslation } from "next-i18next";
import {
  Purchase,
  PurchasePreview,
  PurchasePricing,
  PurchaseProductConfiguration,
} from "@modules/purchase/warranty/types/Purchase";
import PurchaseInformationListItem from "@modules/purchase/warranty/components/PurchaseInformationListItem";
import { useRouter } from "next/router";
import { formatCurrency, dateTimeFormatter } from "@talladega/helpers";
import classNames from "classnames";
import getDisplayCoverageLength from "@modules/purchase/warranty/helpers/getDisplayCoverageLength";
import PurchaseSourceVehicleImage from "@common/components/PurchaseSourceVehicleImage";
import { Button, VehicleAvatar } from "@talladega/components";
import { useConsumerPurchaseLayoutContext } from "@modules/purchase/ConsumerPurchaseLayoutContext";
import { useState } from "react";
import { convert, LocalDate } from "@js-joda/core";
import { hasBrochure } from "@modules/purchase/warranty/types/ExtendedWarrantyCoverageType";
import ApplyPromoCodeModal from "./ApplyPromoCodeModal";
import ZeroPercentFinancingCard from "./ZeroPercentFinancingCard";

type Props = {
  purchase: Purchase;
  purchasePreview?: PurchasePreview;
};

const PurchaseInformation = ({ purchase, purchasePreview }: Props) => {
  const { t } = useTranslation(["purchase", "common"]);
  const router = useRouter();
  const locale = router?.locale || "en";
  const { showCoverageDetailsModalState } = useConsumerPurchaseLayoutContext();
  const [, setShowCoverageDetailsModal] = showCoverageDetailsModalState;

  const [isAddPromoModalOpen, setIsAddPromoModalOpen] = useState(false);

  const getVehicleDetailsList = () => {
    if (purchase && purchase?.vehicle && purchase.vehicle.vin) {
      return (
        <ul
          className={classNames({
            "border-t border-gray-100 pt-3": !!purchase.vehicle,
          })}
        >
          <PurchaseInformationListItem
            key="vehicle-vin"
            label={t("common:vehicle_vin")}
            value={purchase.vehicle.vin}
          />
        </ul>
      );
    }
    return null;
  };

  const getCoveragePlanDetailsList = (config: PurchaseProductConfiguration) => {
    const iAProgram = IAExtendedWarrantyPrograms.find(
      (iap) => iap.id === config.programId
    );

    if (!iAProgram) return null;

    const programI18nKey = iAProgram.coverageTypes.find(
      (ct) => ct.abbr === config.coverageTypeId
    )?.i18nKey;

    const isPremiumCoverage =
      iAProgram.id === ExtendedWarrantyPremiumAutoCanadaDigital.id;

    const term = getDisplayCoverageLength(
      (config.term as number) ?? 0,
      (config.termKms as number) ?? 0,
      locale,
      isPremiumCoverage ? (config.expiryDate as LocalDate) : undefined
    );

    return (
      <ul
        key="plan-details-list"
        className={purchase?.vehicle ? "border-t border-gray-100 pt-3" : ""}
      >
        {programI18nKey && (
          <PurchaseInformationListItem
            key="warranty-plan"
            isHighlighted
            label={t("purchase:warranty_plan")}
            value={t(programI18nKey)}
          />
        )}
        {hasBrochure(config.coverageTypeId as string) && (
          <li>
            <div className="text-md -mx-2 -mt-2 flex justify-end font-semibold">
              <Button
                fill="link"
                spacing="no-padding"
                className="underline hover:no-underline"
                onClick={() => setShowCoverageDetailsModal(true)}
              >
                {t("purchase:view_coverage_details")}
              </Button>
            </div>
          </li>
        )}
        {config.term != null && config.termKms != null && (
          <PurchaseInformationListItem
            key="warranty-term"
            label={t("purchase:coverage_terms_label")}
            value={term}
          />
        )}
        {config.deductible != null && (
          <PurchaseInformationListItem
            key="warranty-deductible"
            label={t("common:deductible")}
            value={formatCurrency(config.deductible as number, locale)}
          />
        )}
      </ul>
    );
  };

  const getCoverageCostDetailsList = (
    config: PurchaseProductConfiguration,
    pricing: PurchasePricing
  ) => {
    const iAProgram = IAExtendedWarrantyPrograms.find(
      (iap) => iap.id === config.programId
    );

    if (!iAProgram) return null;

    const hasValidTaxDetails = pricing.taxes.some((tb) => tb.amount > 0);

    return (
      <>
        <div className="border-t border-gray-100 pt-3">
          <ul key="discount">
            <li className="text-md font-semibold">
              <div className="sm:text-md my-3 flex items-center justify-between gap-2 text-sm sm:font-semibold">
                {pricing.discount > 0 ? (
                  <>
                    <div className="w-3/4 flex items-center">
                      <h5 className="inline pr-1">
                        {t("purchase:discount_applied")}
                      </h5>
                      {purchase.appliedPromoCode && (
                        <div className="inline text-success-700 text-xs md:text-sm border border-success-200 rounded-full ml-2 py-1 px-2 bg-success-50">
                          {purchase.appliedPromoCode?.name}
                        </div>
                      )}
                    </div>
                    <div className="w-1/4 text-right">
                      <h5>{formatCurrency(pricing.discount, locale)}</h5>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="w-3/4 text-primary-500 underline"
                      onClick={() => setIsAddPromoModalOpen(true)}
                      onKeyDown={() => setIsAddPromoModalOpen(true)}
                      role="button"
                      tabIndex={0}
                    >
                      {t("purchase:apply_promo_code").toUpperCase()}
                    </div>
                    <div className="w-1/4 text-right">
                      <h5>{formatCurrency(0, locale)}</h5>
                    </div>
                  </>
                )}
              </div>
            </li>
          </ul>
        </div>

        <ul
          key="cost-details-list"
          className={purchase?.vehicle ? "border-t border-gray-100 pt-3" : ""}
        >
          {pricing.subTotal > 0 && (
            <PurchaseInformationListItem
              key="subtotal"
              label={t("purchase:subtotal")}
              value={formatCurrency(pricing.subTotal, locale)}
            />
          )}
          {hasValidTaxDetails &&
            pricing.taxes.map((tax) => {
              return (
                <PurchaseInformationListItem
                  key={tax.name}
                  label={tax.name}
                  value={formatCurrency(tax.amount, locale)}
                />
              );
            })}
        </ul>
        {pricing.paymentFrequencyId == null && pricing.totalPrice > 0 && (
          <ul
            key="total-cost-list"
            className={purchase?.vehicle ? "border-t border-gray-100 pt-3" : ""}
          >
            <PurchaseInformationListItem
              key="total-cost"
              label={t("common:total_cost")}
              value={formatCurrency(pricing.totalPrice, locale)}
            />
          </ul>
        )}
        {pricing.paymentFrequencyId != null && pricing.payment > 0 && (
          <>
            <ul
              key="due-payment-list"
              className="border-t border-gray-100 pt-3"
            >
              <PurchaseInformationListItem
                key="due-payment"
                label={t("purchase:due_payment")}
                value={formatCurrency(pricing.cashDownAmount ?? 0, locale)}
                valueProps={{ className: "text-2xl font-semibold" }}
                parentProps={{ className: "items-center" }}
              />
            </ul>
            <ul
              key="monthly-payment-list"
              className="border-t border-gray-100 pt-3"
            >
              <PurchaseInformationListItem
                key="monthly-payment"
                label={t("common:monthly_payment")}
                value={formatCurrency(pricing.payment, locale)}
                valueProps={{ className: "text-lg font-semibold" }}
                parentProps={{ className: "items-center mb-1" }}
              />
              <div className="text-sm font-semibold text-gray-600">
                {t("purchase:payments_starting_date", {
                  term: pricing.term ?? 0,
                  date: dateTimeFormatter(
                    convert(LocalDate.now().plusMonths(1)).toDate(),
                    locale ?? "en",
                    "MMM D, YYYY"
                  ),
                })}
              </div>
              <div className="mt-10">
                <ZeroPercentFinancingCard />
              </div>
            </ul>
          </>
        )}
      </>
    );
  };

  const getAllCoveragePlanDetailsList = () => {
    if (purchasePreview != null) {
      return getCoveragePlanDetailsList(purchasePreview.product.config);
    }
    return purchase.products.map((p) => {
      return getCoveragePlanDetailsList(p.config);
    });
  };

  const getAllCoverageCostDetailsList = () => {
    if (purchasePreview != null) {
      return getCoverageCostDetailsList(
        purchasePreview.product.config,
        purchasePreview.pricing
      );
    }
    return (
      purchase &&
      purchase.products.map((p) => {
        return getCoverageCostDetailsList(p.config, purchase.pricing);
      })
    );
  };

  return (
    <>
      <div className="flex flex-col gap-3 lg:hidden">
        {getVehicleDetailsList()}
        {getAllCoveragePlanDetailsList()}
        {getAllCoverageCostDetailsList()}
      </div>

      <div className="items-left mx-6 mb-12 hidden flex-col gap-5 lg:flex 2xl:mx-8">
        <div className="flex w-full flex-col gap-3">
          {purchase.vehicle?.image && (
            <div className="pt-12">
              <PurchaseSourceVehicleImage
                className="max-h-56 w-full rounded-xl object-cover"
                src={purchase.vehicle.image}
                alt="Vehicle picture for purchase"
              />
            </div>
          )}

          <div>
            <h5>{t("purchase:order_summary")}</h5>
          </div>

          <div className="flex gap-3">
            {purchase.vehicle &&
              purchase.vehicle.vin &&
              purchase.vehicle.year && (
                <div>
                  <VehicleAvatar
                    year={purchase.vehicle.year}
                    make={purchase.vehicle.make}
                    bodyStyle={purchase.vehicle.bodyStyle}
                  />
                </div>
              )}
            <div className="w-full pt-4">
              {!!(
                purchase.vehicle &&
                purchase.vehicle.vin &&
                purchase.vehicle.year &&
                purchase.vehicle.make &&
                purchase.vehicle.model
              ) && (
                <h3>
                  {purchase.vehicle.year} {purchase.vehicle.make}{" "}
                  {purchase.vehicle.model}
                </h3>
              )}
              {purchase.vehicle && purchase.vehicle.vin && (
                <div className="mb-3 flex gap-2 text-gray-500">
                  {purchase.vehicle.trim && <h6>{purchase.vehicle.trim}</h6>}
                  {purchase.vehicle.odometerCount > 0 && (
                    <h6>
                      {purchase.vehicle.trim ? " | " : ""}
                      {purchase.vehicle.odometerCount.toLocaleString()}{" "}
                      {t("common:km")}
                    </h6>
                  )}
                </div>
              )}
            </div>
          </div>
          {getVehicleDetailsList()}
          {getAllCoveragePlanDetailsList()}
          {getAllCoverageCostDetailsList()}
        </div>
      </div>
      {isAddPromoModalOpen && (
        <ApplyPromoCodeModal
          isOpen={isAddPromoModalOpen}
          dealId={purchase.id}
          onClose={() => setIsAddPromoModalOpen(false)}
        />
      )}
    </>
  );
};

export default PurchaseInformation;
